import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { ksefService } from '../../services/ksefService';
import type { KSeFAuthMode } from '../../types/businessProfile';

interface KSeFConfigModalProps {
  businessProfileId: string;
  currentEnvironment?: 'Test' | 'Production';
  currentAuthMode?: KSeFAuthMode;
  onClose: () => void;
  onConfigured: () => void;
}

export function KSeFConfigModal({
  businessProfileId,
  currentEnvironment,
  currentAuthMode = 'Token',
  onClose,
  onConfigured,
}: KSeFConfigModalProps) {
  const { t } = useTranslation();
  const { token: authToken } = useAuth();

  // Auth mode selection
  const [authMode, setAuthMode] = useState<KSeFAuthMode>(currentAuthMode);

  // Token mode state
  const [ksefToken, setKsefToken] = useState('');
  const [showToken, setShowToken] = useState(false);

  // Certificate mode state
  const [certificateFile, setCertificateFile] = useState<File | null>(null);
  const [privateKeyFile, setPrivateKeyFile] = useState<File | null>(null);
  const [certificatePassword, setCertificatePassword] = useState('');
  const [showCertPassword, setShowCertPassword] = useState(false);

  // Helper to check if certificate format requires separate key file
  const needsSeparateKeyFile = (file: File | null): boolean => {
    if (!file) return false;
    const extension = file.name.toLowerCase().split('.').pop();
    return ['crt', 'cer', 'pem'].includes(extension || '');
  };

  const isPfxFormat = (file: File | null): boolean => {
    if (!file) return false;
    const extension = file.name.toLowerCase().split('.').pop();
    return ['pfx', 'p12'].includes(extension || '');
  };

  // Common state
  const [environment, setEnvironment] = useState<'Test' | 'Production'>(currentEnvironment || 'Test');
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [isSaving, setIsSaving] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; warning?: boolean } | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const extension = file.name.toLowerCase().split('.').pop();
      const validExtensions = ['pfx', 'p12', 'crt', 'cer', 'pem'];
      if (!extension || !validExtensions.includes(extension)) {
        setValidationErrors(prev => ({
          ...prev,
          certificate: t('ksef.invalidCertificateFormat', 'Wybierz plik certyfikatu w formacie PFX, P12, CRT, CER lub PEM.'),
        }));
        return;
      }
      setCertificateFile(file);
      // Clear key file if switching to PFX format
      if (['pfx', 'p12'].includes(extension)) {
        setPrivateKeyFile(null);
      }
      setValidationErrors(prev => ({ ...prev, certificate: '', privateKey: '' }));
    }
  };

  const handleKeyFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const extension = file.name.toLowerCase().split('.').pop();
      const validExtensions = ['key', 'pem'];
      if (!extension || !validExtensions.includes(extension)) {
        setValidationErrors(prev => ({
          ...prev,
          privateKey: t('ksef.invalidKeyFormat', 'Wybierz plik klucza prywatnego w formacie KEY lub PEM.'),
        }));
        return;
      }
      setPrivateKeyFile(file);
      setValidationErrors(prev => ({ ...prev, privateKey: '' }));
    }
  };

  const validateTokenForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!ksefToken.trim()) {
      errors.token = t('ksef.tokenRequired', 'Token jest wymagany');
    } else if (ksefToken.trim().length < 32) {
      errors.token = t('ksef.tokenTooShort', 'Token jest za krótki');
    }

    if (!environment) {
      errors.environment = t('ksef.environmentRequired', 'Środowisko jest wymagane');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateCertificateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    if (!certificateFile) {
      errors.certificate = t('ksef.certificateRequired', 'Plik certyfikatu jest wymagany');
    }

    // If CRT/CER/PEM format, require separate key file
    if (certificateFile && needsSeparateKeyFile(certificateFile) && !privateKeyFile) {
      errors.privateKey = t('ksef.privateKeyRequired', 'Plik klucza prywatnego jest wymagany dla formatu CRT/CER/PEM');
    }

    // Password is required for PFX format, optional for CRT+KEY
    if (isPfxFormat(certificateFile) && !certificatePassword) {
      errors.certificatePassword = t('ksef.passwordRequired', 'Hasło certyfikatu jest wymagane');
    }

    if (!environment) {
      errors.environment = t('ksef.environmentRequired', 'Środowisko jest wymagane');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveTokenConfiguration = async () => {
    if (!validateTokenForm() || !authToken) return;

    setIsSaving(true);
    setTestResult(null);

    try {
      // Step 1: Save configuration
      await ksefService.configureKSeF(
        businessProfileId,
        { token: ksefToken, environment },
        authToken
      );

      // Step 2: Auto-test connection
      try {
        const testResponse = await ksefService.testKSeFConnection(businessProfileId, authToken);

        if (testResponse.isSuccessful) {
          setTestResult({ success: true, message: testResponse.message });
          setKsefToken('');
          setTimeout(() => {
            onConfigured();
          }, 1500);
        } else {
          setTestResult({ success: false, message: testResponse.message });
        }
      } catch (testError) {
        setTestResult({
          success: false,
          message: testError instanceof Error ? testError.message : t('ksef.testFailed', 'Nie udało się przetestować połączenia'),
        });
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : t('ksef.configFailed', 'Nie udało się skonfigurować KSeF'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCertificateConfiguration = async () => {
    if (!validateCertificateForm() || !authToken || !certificateFile) return;

    setIsSaving(true);
    setTestResult(null);

    try {
      const response = await ksefService.configureKSeFCertificate(
        businessProfileId,
        certificateFile,
        certificatePassword,
        environment,
        authToken,
        privateKeyFile || undefined
      );

      // Check if there's a warning (certificate stored but connection test failed)
      if (response.warning) {
        setTestResult({
          success: true,
          warning: true,
          message: `${t('ksef.certificateStoredWithWarning', 'Certyfikat zapisany, ale wystąpił problem')}: ${response.warning}`,
        });
        // Still clear sensitive data and call onConfigured after a delay
        setCertificatePassword('');
        setCertificateFile(null);
        setPrivateKeyFile(null);

        setTimeout(() => {
          onConfigured();
        }, 3000); // Longer delay so user can read the warning
      } else {
        setTestResult({
          success: true,
          message: `${t('ksef.certificateConfigured', 'Certyfikat skonfigurowany')}: ${response.subject}`,
        });

        // Clear sensitive data
        setCertificatePassword('');
        setCertificateFile(null);
        setPrivateKeyFile(null);

        setTimeout(() => {
          onConfigured();
        }, 1500);
      }
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : t('ksef.certificateConfigFailed', 'Nie udało się skonfigurować certyfikatu'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveConfiguration = () => {
    if (authMode === 'Token') {
      handleSaveTokenConfiguration();
    } else {
      handleSaveCertificateConfiguration();
    }
  };

  const handleClose = () => {
    setKsefToken('');
    setCertificateFile(null);
    setPrivateKeyFile(null);
    setCertificatePassword('');
    setShowToken(false);
    setShowCertPassword(false);
    setValidationErrors({});
    setTestResult(null);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-app-elevated rounded-lg w-full max-w-md max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border-strong bg-gradient-to-r from-brand-primary to-brand-primary-hover">
          <h2 className="text-lg font-semibold text-white">
            {t('ksef.configureTitle', 'Konfiguracja KSeF')}
          </h2>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4 overflow-y-auto">
          {/* Auth Mode Selector */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-ink-primary">
              {t('ksef.authMethod', 'Metoda uwierzytelniania')} *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAuthMode('Token')}
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg border transition-colors ${authMode === 'Token'
                  ? 'bg-brand-primary/15 border-brand-primary text-brand-primary'
                  : 'bg-white/50 border-border-strong text-ink-primary hover:bg-surface-card-hover'
                  }`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                <span className="text-sm font-medium">{t('ksef.authModeToken', 'Token API')}</span>
              </button>
              <button
                type="button"
                onClick={() => setAuthMode('Certificate')}
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-lg border transition-colors ${authMode === 'Certificate'
                  ? 'bg-brand-primary/15 border-brand-primary text-brand-primary'
                  : 'bg-white/50 border-border-strong text-ink-primary hover:bg-surface-card-hover'
                  }`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span className="text-sm font-medium">{t('ksef.certificate', 'Certyfikat')}</span>
              </button>
            </div>
            <p className="text-xs text-ink-muted">
              {authMode === 'Token'
                ? t('ksef.tokenModeDesc', 'Tradycyjna metoda uwierzytelniania tokenem API KSeF')
                : t('ksef.certificateModeDesc', 'Certyfikat kwalifikowany - wymagany od 1 stycznia 2027')}
            </p>
          </div>

          {/* Token Mode Fields */}
          {authMode === 'Token' && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-ink-primary">
                {t('ksef.token', 'Token KSeF')} *
              </label>
              <div className="relative">
                <input
                  type={showToken ? 'text' : 'password'}
                  value={ksefToken}
                  onChange={(e) => setKsefToken(e.target.value)}
                  placeholder={t('ksef.tokenPlaceholder', 'Wprowadź token KSeF')}
                  className={`w-full px-3 py-2 pr-10 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:border-brand-primary ${validationErrors.token ? 'border-brand-warm' : 'border-border-strong'
                    }`}
                />
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-secondary hover:text-white"
                >
                  {showToken ? (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {validationErrors.token && (
                <p className="text-xs text-brand-warm-deep">{validationErrors.token}</p>
              )}
            </div>
          )}

          {/* Certificate Mode Fields */}
          {authMode === 'Certificate' && (
            <>
              {/* Certificate File */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-ink-primary">
                  {t('ksef.certificateFile', 'Plik certyfikatu')} *
                </label>
                <label className={`flex items-center gap-3 p-4 rounded-lg border border-dashed cursor-pointer transition-colors ${validationErrors.certificate
                  ? 'border-brand-warm bg-brand-warm/5'
                  : 'border-border-strong bg-white/30 hover:bg-white/50'
                  }`}>
                  <svg className="w-6 h-6 text-brand-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-ink-primary block truncate">
                      {certificateFile ? certificateFile.name : t('ksef.selectCertificate', 'Wybierz plik certyfikatu')}
                    </span>
                    <span className="text-xs text-ink-muted">PFX, P12, CRT, CER, PEM</span>
                  </div>
                  <input
                    type="file"
                    accept=".pfx,.p12,.crt,.cer,.pem"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                {validationErrors.certificate && (
                  <p className="text-xs text-brand-warm-deep">{validationErrors.certificate}</p>
                )}
              </div>

              {/* Private Key File - Only show when CRT/CER/PEM is selected */}
              {needsSeparateKeyFile(certificateFile) && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-ink-primary">
                    {t('ksef.privateKeyFile', 'Plik klucza prywatnego')} *
                  </label>
                  <label className={`flex items-center gap-3 p-4 rounded-lg border border-dashed cursor-pointer transition-colors ${validationErrors.privateKey
                    ? 'border-brand-warm bg-brand-warm/5'
                    : 'border-border-strong bg-white/30 hover:bg-white/50'
                    }`}>
                    <svg className="w-6 h-6 text-brand-accent flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm text-ink-primary block truncate">
                        {privateKeyFile ? privateKeyFile.name : t('ksef.selectPrivateKey', 'Wybierz plik klucza prywatnego')}
                      </span>
                      <span className="text-xs text-ink-muted">KEY, PEM</span>
                    </div>
                    <input
                      type="file"
                      accept=".key,.pem"
                      onChange={handleKeyFileChange}
                      className="hidden"
                    />
                  </label>
                  {validationErrors.privateKey && (
                    <p className="text-xs text-brand-warm-deep">{validationErrors.privateKey}</p>
                  )}
                </div>
              )}

              {/* Certificate Password - Required for PFX, Optional for CRT+KEY */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-ink-primary">
                  {t('ksef.certificatePassword', 'Hasło certyfikatu')} {isPfxFormat(certificateFile) ? '*' : t('ksef.optional', '(opcjonalne)')}
                </label>
                <div className="relative">
                  <input
                    type={showCertPassword ? 'text' : 'password'}
                    value={certificatePassword}
                    onChange={(e) => setCertificatePassword(e.target.value)}
                    placeholder={t('ksef.passwordPlaceholder', 'Wprowadź hasło certyfikatu')}
                    className={`w-full px-3 py-2 pr-10 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:border-brand-primary ${validationErrors.certificatePassword ? 'border-brand-warm' : 'border-border-strong'
                      }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowCertPassword(!showCertPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-secondary hover:text-white"
                  >
                    {showCertPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {validationErrors.certificatePassword && (
                  <p className="text-xs text-brand-warm-deep">{validationErrors.certificatePassword}</p>
                )}
              </div>

              {/* Info Banner */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-brand-accent/10 border border-brand-accent/30">
                <svg className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-xs text-brand-accent">
                  {t('ksef.certificateInfo', 'Od 1 stycznia 2027 r. uwierzytelnianie tokenem API zostanie wycofane. Certyfikat kwalifikowany będzie jedyną metodą uwierzytelniania w KSeF.')}
                </p>
              </div>
            </>
          )}

          {/* Environment Selector */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-ink-primary">
              {t('ksef.environment', 'Środowisko')} *
            </label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setEnvironment('Test')}
                className={`py-2 px-4 rounded-lg border transition-colors ${environment === 'Test'
                  ? 'bg-brand-primary/15 border-brand-primary text-brand-primary'
                  : 'bg-white/50 border-border-strong text-ink-primary hover:bg-surface-card-hover'
                  }`}
              >
                {t('ksef.environmentTestShort', 'Testowe')}
              </button>
              <button
                type="button"
                onClick={() => setEnvironment('Production')}
                className={`py-2 px-4 rounded-lg border transition-colors ${environment === 'Production'
                  ? 'bg-brand-primary/15 border-brand-primary text-brand-primary'
                  : 'bg-white/50 border-border-strong text-ink-primary hover:bg-surface-card-hover'
                  }`}
              >
                {t('ksef.environmentProductionShort', 'Produkcyjne')}
              </button>
            </div>
            {validationErrors.environment && (
              <p className="text-xs text-brand-warm-deep">{validationErrors.environment}</p>
            )}
          </div>

          {/* Test Result */}
          {testResult && (
            <div className={`flex items-start gap-2 p-3 rounded-lg ${
              testResult.warning
                ? 'bg-brand-accent/10 border border-brand-accent/30'
                : testResult.success
                  ? 'bg-brand-confirm/10 border border-brand-confirm/30'
                  : 'bg-brand-warm/10 border border-brand-warm/30'
              }`}>
              {testResult.warning ? (
                <svg className="w-5 h-5 text-brand-accent flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              ) : testResult.success ? (
                <svg className="w-5 h-5 text-brand-confirm-deep flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-brand-warm-deep flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
              <span className={`text-sm ${
                testResult.warning
                  ? 'text-brand-accent'
                  : testResult.success
                    ? 'text-brand-confirm-deep'
                    : 'text-brand-warm-deep'
              }`}>
                {testResult.message}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-border-strong">
          <button
            type="button"
            onClick={handleClose}
            className="flex-1 py-2 px-4 bg-surface-card text-ink-primary rounded-lg hover:bg-surface-card-hover transition-colors"
          >
            {t('common.cancel', 'Anuluj')}
          </button>
          <button
            type="button"
            onClick={handleSaveConfiguration}
            disabled={isSaving}
            className="flex-1 py-2 px-4 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? t('common.saving', 'Zapisywanie...') : t('ksef.saveConfiguration', 'Zapisz konfigurację')}
          </button>
        </div>
      </div>
    </div>
  );
}
